/**
 * Basketball shooting numbers derived from the advanced-scoring metric totals
 * (PR-B13). The scorer taps made / missed shots per type (catalogue in
 * config/sportsMetrics.js: freeThrowMade 1, twoPointerMade 2, threePointerMade 3,
 * dunk / alleyOop / fastBreakPoint / andOne 2). Field goals are 2PT + 3PT
 * attempts plus the 2-point signature plays; free throws are separate.
 *
 * Nothing is invented: when no attempts were tracked the percentages are null
 * (the profile used to fake FGA as points × 2, i.e. a constant 50% FG).
 */

const n = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
};

const pct = (made, attempts) => (attempts > 0 ? Math.round((made / attempts) * 1000) / 10 : null);

export function basketballShooting(totals = {}) {
  const ftm = n(totals.freeThrowMade);
  const fta = ftm + n(totals.freeThrowMissed);
  const signatureTwos = n(totals.dunk) + n(totals.alleyOop) + n(totals.fastBreakPoint) + n(totals.andOne);
  const twoMade = n(totals.twoPointerMade) + signatureTwos;
  const twoAtt = twoMade + n(totals.twoPointerMissed);
  const tpm = n(totals.threePointerMade);
  const tpa = tpm + n(totals.threePointerMissed);
  const fgm = twoMade + tpm;
  const fga = twoAtt + tpa;

  return {
    points: ftm + 2 * twoMade + 3 * tpm,
    fgm,
    fga,
    fgPct: pct(fgm, fga),
    tpm,
    tpa,
    tpPct: pct(tpm, tpa),
    ftm,
    fta,
    ftPct: pct(ftm, fta),
  };
}

/**
 * Points for a basketball stat line: the stored points_scored total
 * (catalogue-weighted since AS-B02) when present, else the points the made
 * shots add up to, else a legacy `points` counter.
 */
export function basketballPoints(totals = {}, scoreContribution = 0) {
  const stored = n(scoreContribution);
  if (stored > 0) {
    return stored;
  }
  const derived = basketballShooting(totals).points;
  return derived > 0 ? derived : n(totals.points);
}

export default basketballShooting;
